import { ChevronRight } from 'lucide-react';
import { Fragment, useEffect, type ReactNode } from 'react';
import { useLocation } from 'react-router';
import { NavigationLink } from '../lib/navigationMemory';
import { useEmbedded, useEmbeddedLabel } from '../lib/scopedParams';
import { useHubLabels } from '../nav/hubLabels';
import { findHub, hubUrl, locate } from '../nav/hubs';

export interface Crumb {
  label: string;
  to?: string;
}

/**
 * Breadcrumbs of an object page: "Hub › Tab › Object". The page's own trail is kept from the
 * first crumb after its list page (e.g. a customer between the list and one of its users).
 */
export function useHubCrumbs(crumbs: Crumb[] | undefined, title: ReactNode): Crumb[] | undefined {
  const { pathname, search } = useLocation();
  const labels = useHubLabels();
  const here = locate(pathname, search);
  if (!here || here.exact) return crumbs;
  const hub = findHub(here.hub);
  const hubCrumbs: Crumb[] = [{ label: labels.hub(here.hub), to: hub?.path ?? '/' }, { label: labels.tab(here.hub, here.tab), to: hubUrl(here.hub, here.tab) }];
  if (here.sub) hubCrumbs.push({ label: labels.sub(here.hub, here.tab, here.sub), to: hubUrl(here.hub, here.tab, here.sub) });
  const own = crumbs ?? [];
  let last = -1;
  own.forEach((c, i) => {
    if (c.to && locate(c.to.split('?')[0] ?? '', c.to.split('?')[1] ?? '')?.exact) last = i;
  });
  const tail = last >= 0 ? own.slice(last + 1) : own.filter((c) => c.to);
  if (!tail.length && typeof title === 'string' && title) tail.push({ label: title });
  return [...hubCrumbs, ...tail];
}

/**
 * Page title block of the reference layouts: breadcrumb, title, subtitle (on object pages the
 * ID line: code · kind · owner), a row of status `badges`, actions on the right (primary
 * first), extra content below. Inside a hub tab or an object-page tab (`EmbeddedPage`) it is a
 * section heading instead: no breadcrumb, a smaller title (none when it only repeats the tab's
 * name), the same subtitle and actions.
 */
export function PageHeader({
  title,
  subtitle,
  crumbs,
  actions,
  badges,
  children,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  crumbs?: Crumb[];
  actions?: ReactNode;
  /** Status badges shown under the title (object pages). */
  badges?: ReactNode;
  children?: ReactNode;
}) {
  const embedded = useEmbedded();
  const tabLabel = useEmbeddedLabel();
  const trail = useHubCrumbs(crumbs, title);
  if (embedded) {
    const repeat = typeof title === 'string' && title === tabLabel;
    return (
      <header className="mb-5 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            {repeat ? null : <h2 className="text-lg font-semibold tracking-tight break-words">{title}</h2>}
            {subtitle ? <div className="text-sm text-muted-foreground">{subtitle}</div> : null}
          </div>
          {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
        </div>
        {children}
      </header>
    );
  }
  return (
    <header className="mb-5 space-y-2">
      <Crumbs crumbs={trail} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0 space-y-1.5">
          <h1 className="text-[26px] leading-tight font-semibold tracking-tight break-words">{title}</h1>
          {subtitle ? <div className="text-[13px] text-muted-foreground">{subtitle}</div> : null}
        </div>
        {actions ? <div className="flex flex-wrap items-center gap-2">{actions}</div> : null}
      </div>
      {badges ? <div className="flex flex-wrap items-center gap-2 pt-1">{badges}</div> : null}
      {children}
    </header>
  );
}

export function Crumbs({ crumbs }: { crumbs?: Crumb[] }) {
  if (!crumbs?.length) return null;
  return (
    <nav className="flex flex-wrap items-center gap-1 text-xs text-muted-foreground" aria-label="Breadcrumb">
      {crumbs.map((c, i) => (
        <Fragment key={`${c.label}-${i}`}>
          {i > 0 ? <ChevronRight className="size-3.5" aria-hidden="true" /> : null}
          {c.to && i < crumbs.length - 1 ? (
            <NavigationLink to={c.to} className="hover:text-foreground">
              {c.label}
            </NavigationLink>
          ) : (
            <span className={i === crumbs.length - 1 ? 'text-foreground' : undefined}>{c.label}</span>
          )}
        </Fragment>
      ))}
    </nav>
  );
}

/** Sets `document.title` as "<page> · Control Center". Inside a hub tab the hub sets it. */
export function useDocumentTitle(title: string): void {
  const embedded = useEmbedded();
  useEffect(() => {
    if (embedded) return;
    document.title = title ? `${title} · Control Center` : 'Registrar Control Center';
  }, [title, embedded]);
}
